import {IColumnDefOf, IUiGridConstants} from "ui-grid";
import {EquityPositionViewModel} from "../../models/EquityPositionViewModel";
import {ObjectUtil} from "../../../../js/util/ObjectUtil";
import {translate} from "angular";

interface EquityGridColumnConfig {
    quantity: IColumnDefOf<EquityPositionViewModel>;
    security: IColumnDefOf<EquityPositionViewModel>;
    securityDescription: IColumnDefOf<EquityPositionViewModel>;
    averagePrice: IColumnDefOf<EquityPositionViewModel>;
    latestPrice: IColumnDefOf<EquityPositionViewModel>;
    cost: IColumnDefOf<EquityPositionViewModel>;
    marketValue: IColumnDefOf<EquityPositionViewModel>;
    todayPnL: IColumnDefOf<EquityPositionViewModel>;
    change: IColumnDefOf<EquityPositionViewModel>;
    changePercent: IColumnDefOf<EquityPositionViewModel>;
    pAndL: IColumnDefOf<EquityPositionViewModel>;
    pnlPercent: IColumnDefOf<EquityPositionViewModel>;
    currentDividendExDate: IColumnDefOf<EquityPositionViewModel>;
    currentDividendPayableDate: IColumnDefOf<EquityPositionViewModel>;
    dividendForecast: IColumnDefOf<EquityPositionViewModel>;
    yearPriceHigh: IColumnDefOf<EquityPositionViewModel>;
    yearPriceLow: IColumnDefOf<EquityPositionViewModel>;
    industryGroup: IColumnDefOf<EquityPositionViewModel>;
    marketCap: IColumnDefOf<EquityPositionViewModel>;
    peRatio: IColumnDefOf<EquityPositionViewModel>;
}

interface EquityGridColumnPreset {
    readonly columns: Array<IColumnDefOf<EquityPositionViewModel>>;
}

interface EquityGridColumnPresets {
    readonly mobile: EquityGridColumnPreset;
    readonly tablet: EquityGridColumnPreset;
    readonly condensed: EquityGridColumnPreset;
    readonly desktop: EquityGridColumnPreset;
}

function equityGridColumns(translateService: translate.ITranslateService, uiGridConstants: IUiGridConstants): EquityGridColumnConfig {
    return {
        quantity: {
            name: "quantity",
            type: "number",
            displayName: translateService.instant("equity_positions.QUANTITY"),
            cellFilter: "number:0",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: "gridNumber",
            cellTemplate: `<div class="ui-grid-cell-contents"><tf-order-entry-link security="row.entity" quantity="row.entity.quantity" side="'LONG_SELL'">{{COL_FIELD | number : 0}}</tf-order-entry-link><div>`,
            enableFiltering: false,
            enableColumnResizing: true,
            visible: false,
            width: "**"
        },
        security: {
            name: "security",
            field: "code",
            type: "string",
            displayName: translateService.instant("equity_positions.SECURITY"),
            headerCellFilter: "translate",
            width: "**",
            visible: false,
            cellTemplate: `<div class="ui-grid-cell-contents"><tf-security-link security="row.entity"></tf-security-link><div>`,
            sort: {
                priority: 1,
                direction: "asc"
            }
        },
        securityDescription: {
            name: "securityDescription",
            type: "string",
            displayName: translateService.instant("equity_positions.SECURITY_DESCRIPTION"),
            headerCellFilter: "translate",
            width: "**",
            visible: false
        },
        averagePrice: {
            name: "averagePrice",
            type: "number",
            displayName: translateService.instant("equity_positions.AVERAGE_PRICE"),
            cellFilter: "number:6",
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                if (gridRow?.entity.pendingAveragePriceUpdate != null) {
                    return "gridNumber pending-update";
                } else {
                    return "gridNumber";
                }
            },
            cellTemplate: `<div class="ui-grid-cell-contents"><tf-average-price-update-request-link position="row.entity" on-update="grid.appScope.ctrl.averagePriceUpdated()">{{COL_FIELD | priceInCents : false : 6}}</tf-average-price-update-request-link><div>`,
            visible: false
        },
        latestPrice: {
            name: "last",
            type: "number",
            displayName: translateService.instant("equity_positions.LAST"),
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: "gridNumber",
            enableFiltering: false,
            cellTemplate: `<div class="ui-grid-cell-contents"><span tf-quote-change="row.entity.quote">{{COL_FIELD | strategyPrice : row.entity.quote.strategy}}</span><div>`,
            visible: false
        },
        cost: {
            name: "cost",
            type: "number",
            displayName: translateService.instant("equity_positions.COST"),
            cellFilter: "ledger:0",
            footerCellFilter: "ledger:0",
            footerCellClass: "gridNumber",
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: "gridNumber",
            enableFiltering: false,
            visible: false,
            aggregationType: uiGridConstants.aggregationTypes.sum,
            aggregationHideLabel: true
        },
        marketValue: {
            name: "totalValue",
            type: "number",
            displayName: translateService.instant("equity_positions.MARKET_VALUE"),
            cellFilter: "ledger:0",
            footerCellFilter: "ledger:0",
            footerCellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                return `gridNumber ${+gridCol.getAggregationValue() < 0 ? "negative" : "positive"}`;
            },
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                if (gridRow?.entity.totalValue == null) {
                    return "gridNumber not-available";
                } else if (gridRow.entity.totalValue === 0) {
                    return "gridNumber";
                } else if (gridRow.entity.totalValue < 0) {
                    return "gridNumber negative";
                } else {
                    return "gridNumber positive";
                }
            },
            enableFiltering: false,
            visible: false,
            aggregationType: uiGridConstants.aggregationTypes.sum,
            aggregationHideLabel: true
        },
        todayPnL: {
            name: "todayPnL",
            type: "number",
            displayName: translateService.instant("equity_positions.TODAY_PNL"),
            cellFilter: "ledger:0",
            footerCellFilter: "ledger:0",
            footerCellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                return `gridNumber ${+gridCol.getAggregationValue() < 0 ? "negative" : "positive"}`;
            },
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                if (gridRow?.entity.todayPnL == null) {
                    return "gridNumber not-available";
                } else if (gridRow.entity.todayPnL === 0) {
                    return "gridNumber";
                } else if (gridRow.entity.todayPnL < 0) {
                    return "gridNumber negative";
                } else {
                    return "gridNumber positive";
                }
            },
            enableFiltering: false,
            visible: false,
            aggregationType: uiGridConstants.aggregationTypes.sum,
            aggregationHideLabel: true
        },
        change: {
            name: "change",
            type: "number",
            displayName: translateService.instant("equity_positions.CHANGE"),
            cellFilter: "number:3",
            cellTemplate: `<div class="ui-grid-cell-contents">{{COL_FIELD | strategyPrice : row.entity.priceStrategy}}<div>`,
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                if (gridRow?.entity.change == null) {
                    return "gridNumber not-available";
                } else if (gridRow.entity.change === 0) {
                    return "gridNumber";
                } else if (gridRow.entity.change < 0) {
                    return "gridNumber negative";
                } else {
                    return "gridNumber positive";
                }
            },
            enableFiltering: false,
            visible: false
        },
        changePercent: {
            name: "changePercent",
            type: "number",
            displayName: translateService.instant("equity_positions.CHANGE_PERCENT"),
            cellFilter: "percentage:2",
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                if (gridRow?.entity.changePercent == null) {
                    return "gridNumber not-available";
                } else if (gridRow.entity.changePercent === 0) {
                    return "gridNumber";
                } else if (gridRow.entity.changePercent < 0) {
                    return "gridNumber negative";
                } else {
                    return "gridNumber positive";
                }
            },
            enableFiltering: false,
            visible: false
        },
        pAndL: {
            name: "pnl",
            type: "number",
            displayName: translateService.instant("equity_positions.PNL"),
            cellFilter: "ledger:0 | otherwise: 'N/A'",
            footerCellFilter: "ledger:0",
            footerCellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                return `gridNumber ${+gridCol.getAggregationValue() < 0 ? "negative" : "positive"}`;
            },
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                if (gridRow?.entity.pnl == null) {
                    return "gridNumber not-available";
                } else if (gridRow.entity.pnl === 0) {
                    return "gridNumber";
                } else if (gridRow.entity.pnl < 0) {
                    return "gridNumber negative";
                } else {
                    return "gridNumber positive";
                }
            },
            enableFiltering: false,
            visible: false,
            aggregationType: uiGridConstants.aggregationTypes.sum,
            aggregationHideLabel: true
        },
        pnlPercent: {
            name: "pnlPercent",
            type: "number",
            displayName: translateService.instant("equity_positions.PNL_PERCENT"),
            cellFilter: "percentage : 2 | otherwise: 'N/A'",
            width: "**",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: (grid, gridRow, gridCol, rowRenderIndex, colRenderIndex) => {
                if (gridRow?.entity.pnlPercent == null) {
                    return "gridNumber not-available";
                } else if (gridRow.entity.pnlPercent === 0) {
                    return "gridNumber";
                } else if (gridRow.entity.pnlPercent < 0) {
                    return "gridNumber negative";
                } else {
                    return "gridNumber positive";
                }
            },
            enableFiltering: false,
            visible: false
        },
        currentDividendExDate: {
            name: "currentDividendExDate",
            type: "string",
            displayName: translateService.instant("equity_positions.DIVIDEND_EX_DATE"),
            headerCellFilter: "translate",
            cellTemplate: `<div class="ui-grid-cell-contents" ng-class="{'dividend-ex-period': row.entity.currentDividendExPeriod}"><span ng-if="row.entity.currentDividendExDate == null" class="alt-text">N/A</span><span ng-if="row.entity.currentDividendExDate != null">{{COL_FIELD | date : 'dd MMM yyyy'}}</span><div>`,
            width: "**",
            visible: false
        },
        currentDividendPayableDate: {
            name: "currentDividendPayableDate",
            type: "string",
            displayName: translateService.instant("equity_positions.DIVIDEND_PAYABLE_DATE"),
            headerCellFilter: "translate",
            cellTemplate: `<div class="ui-grid-cell-contents" ng-class="{'dividend-not-paid': row.entity.currentDividendNotPaid}"><span ng-if="row.entity.currentDividendPayableDate == null" class="alt-text">N/A</span><span ng-if="row.entity.currentDividendPayableDate != null">{{COL_FIELD | date : 'dd MMM yyyy'}}</span><div>`,
            width: "**",
            visible: false
        },
        dividendForecast: {
            name: "dividendForecast",
            type: "number",
            displayName: translateService.instant("equity_positions.DIVIDEND_VALUE"),
            cellFilter: "ledger:2",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: "gridNumber",
            enableFiltering: false,
            cellTemplate: `<div class="ui-grid-cell-contents" ng-class="{'dividend-not-paid': row.entity.currentDividendNotPaid}"><span ng-if="row.entity.dividendForecast == null" class="alt-text">N/A</span><span ng-if="row.entity.dividendForecast != null">{{COL_FIELD | strategyPrice : row.entity.priceStrategy}}</span><div>`,
            visible: false
        },
        yearPriceHigh: {
            name: "yearPriceHigh",
            type: "number",
            displayName: translateService.instant("equity_positions.YEAR_PRICE_HIGH"),
            cellFilter: "number:3",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: "gridNumber",
            enableFiltering: false,
            cellTemplate: `<div class="ui-grid-cell-contents">{{COL_FIELD | strategyPrice : row.entity.priceStrategy}}<div>`,
            visible: false
        },
        yearPriceLow: {
            name: "yearPriceLow",
            type: "number",
            displayName: translateService.instant("equity_positions.YEAR_PRICE_LOW"),
            cellFilter: "number:3",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: "gridNumber",
            enableFiltering: false,
            cellTemplate: `<div class="ui-grid-cell-contents">{{COL_FIELD | strategyPrice : row.entity.priceStrategy}}<div>`,
            visible: false
        },
        industryGroup: {
            name: "industryGroup",
            type: "string",
            displayName: translateService.instant("equity_positions.INDUSTRY_GROUP"),
            headerCellFilter: "translate",
            cellFilter: "industryGroupTranslate | translate",
            width: "**",
            visible: false
        },
        marketCap: {
            name: "marketCap",
            type: "number",
            displayName: translateService.instant("equity_positions.MARKET_CAPITALIZATION"),
            cellFilter: "thousandSuffix:2",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: "gridNumber",
            enableFiltering: false,
            visible: false
        },
        peRatio: {
            name: "peRatio",
            type: "number",
            displayName: translateService.instant("equity_positions.PE_RATIO"),
            cellFilter: "number:3",
            headerCellClass: "gridNumber",
            headerCellFilter: "translate",
            cellClass: "gridNumber",
            enableFiltering: false,
            visible: false
        }
    };
}

function mobileColumns(translateService: translate.ITranslateService, uiGridConstants: IUiGridConstants): Array<IColumnDefOf<EquityPositionViewModel>> {
    const columns = ObjectUtil.merge({}, equityGridColumns(translateService, uiGridConstants), {
        quantity: {
            visible: true
        },
        security: {
            visible: true
        },
        latestPrice: {
            visible: true
        },
        pAndL: {
            visible: true
        },
        pnlPercent: {
            visible: true
        }
    });
    return Object.values(columns);
}

function tabletColumns(translateService: translate.ITranslateService, uiGridConstants: IUiGridConstants): Array<IColumnDefOf<EquityPositionViewModel>> {
    const columns = ObjectUtil.merge({}, equityGridColumns(translateService, uiGridConstants), {
        quantity: {
            visible: true
        },
        security: {
            visible: true
        },
        latestPrice: {
            visible: true
        },
        pAndL: {
            visible: true
        },
        pnlPercent: {
            visible: true
        },
        securityDescription: {
            visible: true
        },
        cost: {
            visible: true
        },
        marketValue: {
            visible: true
        },
        todayPnL: {
            visible: true
        }
    });
    return Object.values(columns);
}

function condensedColumns(translateService: translate.ITranslateService, uiGridConstants: IUiGridConstants): Array<IColumnDefOf<EquityPositionViewModel>> {
    const columns = ObjectUtil.merge({}, equityGridColumns(translateService, uiGridConstants), {
        quantity: {
            visible: true
        },
        security: {
            visible: true
        },
        averagePrice: {
            visible: true
        },
        latestPrice: {
            visible: true
        },
        pAndL: {
            visible: true
        },
        pnlPercent: {
            visible: true
        },
        securityDescription: {
            visible: true
        },
        cost: {
            visible: true
        },
        marketValue: {
            visible: true
        },
        todayPnL: {
            visible: true
        },
        change: {
            visible: true
        },
        changePercent: {
            visible: true
        },
        currentDividendExDate: {
            visible: true
        },
        dividendForecast: {
            visible: true
        },
        yearPriceHigh: {
            visible: true
        },
        yearPriceLow: {
            visible: true
        }
    });
    return Object.values(columns);
}

function desktopColumns(translateService: translate.ITranslateService, uiGridConstants: IUiGridConstants): Array<IColumnDefOf<EquityPositionViewModel>> {
    const allColumns = Object.values(equityGridColumns(translateService, uiGridConstants));
    return allColumns.map(column => ObjectUtil.merge({}, column, { visible: true }));
}

export function equityColumnPresets(translateService: translate.ITranslateService, uiGridConstants: IUiGridConstants): EquityGridColumnPresets {
    return {
        mobile: {
            columns: mobileColumns(translateService, uiGridConstants)
        },
        tablet: {
            columns: tabletColumns(translateService, uiGridConstants)
        },
        condensed: {
            columns: condensedColumns(translateService, uiGridConstants)
        },
        desktop: {
            columns: desktopColumns(translateService, uiGridConstants)
        }
    };
}
